import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { DEFAULT_SIDE_PANEL_ROUTE_STATE, FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { useAppSelector } from 'hooks';
import SidePanel from 'components/side-panel/SidePanel';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { SidePanelRouteState } from 'routes/types';
import EstimateDetailsPanel from './EstimateDetailsPanel';
import EditEstimateDescriptionPanel from './EditEstimateDescriptionPanel';
import EstimateTimelinePanel from './EstimateTimelinePanel';
import EditEstimateStatusPanel from './EditEstimateStatusPanel';
import { Estimate, EstimateTimelineEvent } from '../estimates.model';
import {
  useDeleteEstimateMutation,
  useDeleteEstimateNoteMutation,
  useLazyGetEstimateByIdQuery,
  useLazyGetEstimateTimelineQuery,
  useUpdateEstimateAssigneesMutation,
  useUpdateEstimateAttachmentsMutation,
  useUpdateEstimateDescriptionMutation,
  useUpdateEstimateExpiryDateMutation,
  useUpdateEstimateLeadSourceMutation,
  useUpdateEstimateNoteMutation,
  useUpdateEstimateRemindersMutation,
  useUpdateEstimateStatusMutation,
  useUpdateEstimateTncMutation
} from '../api';
import routesPath from 'routes/RoutesPath';
import GenericDeleteModal from 'components/generic-delete-modal/GenericDeleteModal';
import CreateEditEstimateNoteModal from './CreateEditEstimateNoteModal';
import { useLazyGetAssignableMembersQuery } from 'containers/jobs/api';
import { AssignableMember } from 'containers/jobs/jobs.model';
import AssignableMembersModal from 'containers/jobs/create-edit-job/components/AssignableMembersModal';
import DateUtils from 'utils/dateUtils';
import { EstimateStatus } from '../constants';
import EditEstimateStatusDetailPanel from './EditEstimateStatusDetailPanel';
import EditLeadSourcePanel from 'containers/jobs/components/EditLeadSourcePanel';
import EditEstimateTncPanel from './EditEstimateTncPanel';
import { logAnalyticEvent } from 'utils/analytics';
import { EstimatesCleverTapEvents } from '../events';
import EditEstimateNotePanel from './EditEstimateNotePanel';
import AttachmentViewDetailsPanel from './AttachmentViewDetailsPanel';
import EditExpiryDatePanel from './EditExpiryDatePanel';
import EditReminderModal from 'components/reminder-modal/EditReminderModal';
import { ReminderConfig } from 'components/reminder-modal/types';
import { ReminderDateTypeLabel } from 'containers/settings/constants';
import { transformEstimateApiRemindersToComponentFormat, transformEstimateRemindersToApiFormat } from '../utils';

enum EstimatePanelPages {
  DETAILS = 'details',
  EDIT_DESCRIPTION = 'edit-description',
  HISTORY = 'history',
  STATUS = 'status',
  STATUS_DETAILS = 'status-details',
  LEAD_SOURCE = 'lead-source',
  TNC = 'tnc',
  NOTE = 'note',
  ATTACHMENT = 'attachment',
  EXPIRY_DATE = 'expiry-date'
}

const EstimatesPanel: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [isRootState, setIsRootState] = useState(true);
  const { handleSidePanelClose, refetchEstimatesList } = useOutletContext<any>();
  const [estimate, setEstimate] = useState<Estimate>();
  const [currentPage, setCurrentPage] = useState(EstimatePanelPages.DETAILS);
  const [currentActionsPopoverId, setCurrentActionsPopoverId] = useState<number | null>(null);
  const [notesInput, setNotesInput] = useState('');
  const [assignableTeamMembers, setAssignableTeamMembers] = useState<AssignableMember[]>([]);
  const [openJobAssigneesModal, setOpenJobAssigneesModal] = useState(false);
  const [editedStatus, setEditedStatus] = useState<EstimateStatus | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);

  const { rootEstimateId, estimateId } = useParams();

  const [getEstimateByIdQuery, estimateByIdState] = useLazyGetEstimateByIdQuery();
  const {
    data: estimateData,
    isFetching: isEstimateByIdFetching,
    isSuccess: isEstimateByIdSuccess,
    isError: isEstimateByIdError
  } = estimateByIdState;

  const [getEstimateTimelineQuery, { data: timelineData }] = useLazyGetEstimateTimelineQuery();

  const [updateEstimateExpiryDate, { isLoading: isUpdateEstimateExpiryDateLoading }] =
    useUpdateEstimateExpiryDateMutation();
  const [updateEstimateDescription] = useUpdateEstimateDescriptionMutation();
  const [deleteEstimateMutation, { isLoading: isDeleteEstimateLoading }] = useDeleteEstimateMutation();
  const [deleteEstimateNoteMutation] = useDeleteEstimateNoteMutation();
  const [updateEstimateStatus, { isLoading: isUpdateEstimateStatusLoading }] = useUpdateEstimateStatusMutation();
  const [updateEstimateAssignees] = useUpdateEstimateAssigneesMutation();
  const [updateEstimateLeadSource, { isLoading: isUpdateEstimateLeadSourceLoading }] =
    useUpdateEstimateLeadSourceMutation();
  const [updateEstimateTnc] = useUpdateEstimateTncMutation();
  const [updateEstimateNote] = useUpdateEstimateNoteMutation();
  const [updateEstimateAttachments] = useUpdateEstimateAttachmentsMutation();
  const [updateEstimateReminders, { isLoading: isUpdateEstimateRemindersLoading }] =
    useUpdateEstimateRemindersMutation();

  const [getAssignableTeamMemberQuery, { data: assignableTeamMembersData, isSuccess: assignableTeamMembersSuccess }] =
    useLazyGetAssignableMembersQuery();

  const getEstimateById = (estimateIdParam: string) => {
    getEstimateByIdQuery({ estimateId: estimateIdParam, teamId: currentTeam.id });
  };

  const getEstimateTimeline = (estimateIdParam: string) => {
    getEstimateTimelineQuery({
      teamId: currentTeam.id,
      estimateId: estimateIdParam,
      timezone: DateUtils.getTimezoneLongName()
    });
  };

  useEffect(() => {
    if ((estimateId || rootEstimateId) && currentTeam?.id) {
      getEstimateById(estimateId ? estimateId : rootEstimateId);
      // Trigger the lazy query for the timeline
      getEstimateTimeline(estimateId || rootEstimateId);
    }
  }, [estimateId, rootEstimateId, currentTeam]);

  useEffect(() => {
    if (isEstimateByIdSuccess && !isEstimateByIdFetching) {
      setEstimate(estimateData.data);
    }
  }, [isEstimateByIdSuccess, isEstimateByIdFetching]);

  useEffect(() => {
    let routeStateTimeout;
    const routeState: SidePanelRouteState = location?.state ?? {};
    if (routeState?.fromSidePanel) {
      setIsRootState(false);
    }
    if (routeState?.shouldAnimate) {
      routeStateTimeout = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: DEFAULT_SIDE_PANEL_ROUTE_STATE });
      }, 800);
    }
    return () => {
      clearTimeout(routeStateTimeout);
    };
  }, [location]);

  const handleCustomerClick = () => {
    if (estimate?.customer?.id) {
      navigate(`${routesPath.ESTIMATES}/${currentTeam.id}/${rootEstimateId}/customer/${estimate.customer.id}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  const handleEditDescriptionClick = () => {
    setCurrentPage(EstimatePanelPages.EDIT_DESCRIPTION);
  };

  const handleEditExpirationClick = () => {
    setCurrentPage(EstimatePanelPages.EXPIRY_DATE);
  };

  const handleSaveExpiryDate = async (newExpiryDate: Date | null) => {
    if (estimate && currentTeam?.id) {
      try {
        await updateEstimateExpiryDate({
          teamId: currentTeam.id,
          estimateId: estimate.id,
          expiresAt: newExpiryDate ? newExpiryDate.toISOString() : ''
        }).unwrap();
        getEstimateById(estimate.id);
        handleRefetchEstimatesList();
        goBackToDetails();
      } catch (error) {
        // Handle error
      }
    }
  };

  const handleSaveDescription = async (newDescription: string) => {
    if (estimate && currentTeam?.id) {
      try {
        await updateEstimateDescription({
          teamId: currentTeam.id,
          estimateId: estimate.id,
          description: newDescription
        }).unwrap();
        refreshEstimateDetails();
      } catch (error) {
        // Handle error
      }
    }
  };

  const handleRefetchEstimatesList = () => {
    if (refetchEstimatesList) {
      refetchEstimatesList();
    }
  };

  const handleDeleteEstimate = async () => {
    if (estimate && currentTeam?.id) {
      try {
        await deleteEstimateMutation({
          teamId: currentTeam.id,
          estimateId: estimate.id
        }).unwrap();
        handleRefetchEstimatesList();
        handleSidePanelClose();
        logAnalyticEvent(EstimatesCleverTapEvents.webDeleteEstimateConfirmation);
      } catch (error) {
        // Handle error
      } finally {
        setShowDeleteModal(false);
      }
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const goBackToDetails = () => {
    setCurrentPage(EstimatePanelPages.DETAILS);
  };

  const handleMarkAsExpired = async () => {
    if (estimate && currentTeam?.id) {
      try {
        await updateEstimateStatus({
          teamId: currentTeam.id,
          estimateId: estimate.id,
          status: EstimateStatus.EXPIRED
        }).unwrap();
        getEstimateById(estimate.id);
      } catch (error) {
        // Handle error
      }
    }
  };

  const handleArchiveEstimate = () => {
    setEditedStatus(EstimateStatus.ARCHIVED);
    setCurrentPage(EstimatePanelPages.STATUS_DETAILS);
  };

  const handleMarkAsAccepted = () => {
    setEditedStatus(EstimateStatus.ACCEPTED);
    setCurrentPage(EstimatePanelPages.STATUS_DETAILS);
  };

  const [isEditNoteModalOpen, setIsEditNoteModalOpen] = useState({ isOpen: false, isEdit: false });
  const [currentNote, setCurrentNote] = useState<EstimateTimelineEvent | null>(null);

  const handleEditNoteClick = (event: EstimateTimelineEvent) => {
    setCurrentNote(event);
    setIsEditNoteModalOpen({ isOpen: true, isEdit: true });
  };

  const handleAddNoteClick = () => {
    setCurrentNote(null);
    setIsEditNoteModalOpen({ isEdit: false, isOpen: true });
  };

  const handleCloseEditNoteModal = () => {
    setIsEditNoteModalOpen({ isEdit: false, isOpen: false });
    setCurrentNote(null);
  };

  const handleDeleteNoteClick = async (event: EstimateTimelineEvent) => {
    if (estimate && currentTeam?.id) {
      try {
        await deleteEstimateNoteMutation({
          teamId: currentTeam.id,
          estimateId: estimate.id,
          noteId: event.noteId
        }).unwrap();
        getEstimateTimeline(estimate.id);
      } catch (error) {
        // Handle error
      }
    }
  };

  const handleViewHistoryClick = () => {
    setCurrentPage(EstimatePanelPages.HISTORY);
    logAnalyticEvent(EstimatesCleverTapEvents.webEstimateHistoryPanel);
  };

  useEffect(() => {
    if (currentTeam?.id) {
      getAssignableTeamMemberQuery(currentTeam?.id.toString());
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    if (assignableTeamMembersSuccess) {
      setAssignableTeamMembers(assignableTeamMembersData.data.teamMemberList);
    }
  }, [assignableTeamMembersSuccess, assignableTeamMembersData]);

  const getTeamMemberNames = () => {
    let remainingMemberCount = 0;
    if (estimate?.assignees?.length > 0) {
      let teamMemberNames = '';

      for (let index = 0; index < estimate?.assignees.length; index++) {
        const nameToAdd = estimate?.assignees[index].name;
        if (teamMemberNames.length < 27) {
          teamMemberNames += `${teamMemberNames.length > 0 ? ', ' : ''}${nameToAdd}`;
        } else {
          remainingMemberCount = estimate?.assignees.length - index;
          break;
        }
      }

      return (
        <div className="flex items-center">
          <div className="max-w-[190px] overflow-hidden text-ellipsis whitespace-nowrap">
            {`${teamMemberNames.trim()}`}
          </div>
          {remainingMemberCount > 0 && <div className="ml-1 text-secondaryBtnText">{`+${remainingMemberCount}`}</div>}
        </div>
      );
    }
    return '';
  };

  const handleEditAssigneesClick = () => {
    setOpenJobAssigneesModal(true); // Open the modal for editing assignees
  };

  const handleCloseAssignMembersModal = () => {
    setOpenJobAssigneesModal(false); // Close the modal
  };

  const handleSaveAssignees = async (membersList: AssignableMember[]) => {
    const assigneeIds = membersList.map(member => member.merchantId); // Extract the IDs of the selected members
    try {
      await updateEstimateAssignees({
        teamId: currentTeam.id,
        estimateId: estimate.id,
        assignees: assigneeIds // Pass the IDs to the API
      }).unwrap(); // Use unwrap to handle the promise
      getEstimateById(estimate.id);
      setOpenJobAssigneesModal(false); // Close the modal
    } catch (error) {
      // Handle error (e.g., show a notification)
    }
  };

  const handleEditStatusClick = () => {
    setCurrentPage(EstimatePanelPages.STATUS);
  };

  const handleUpdateStatus = async (note: string) => {
    await updateEstimateStatus({
      teamId: currentTeam.id,
      estimateId: estimate.id,
      status: editedStatus,
      comment: note?.trim() || ''
    }).unwrap();
    getEstimateTimeline(estimate.id);
    handleRefetchEstimatesList();
    refreshEstimateDetails();
  };

  const refreshEstimateDetails = () => {
    getEstimateById(estimate.id);
    goBackToDetails();
  };

  const handleChangeLeadSource = async (newLeadSourceType: string, newLeadSource: string) => {
    if (estimate && currentTeam?.id) {
      try {
        await updateEstimateLeadSource({
          estimateId: estimate.id,
          leadSource: newLeadSource,
          leadSourceType: newLeadSourceType,
          teamId: currentTeam.id
        }).unwrap(); // Use unwrap to handle the promise
        refreshEstimateDetails();
      } catch (error) {
        // Handle error (e.g., show a notification)
      }
    }
  };

  const handleEditLeadSourceClick = () => {
    setCurrentPage(EstimatePanelPages.LEAD_SOURCE);
    logAnalyticEvent(EstimatesCleverTapEvents.webEstimateLeadSourcePageView);
  };

  const handleEditTncClick = () => {
    setCurrentPage(EstimatePanelPages.TNC);
  };

  const handleEditEstimateNoteClick = () => {
    setCurrentPage(EstimatePanelPages.NOTE);
  };

  const handleAttachmentDetailClick = () => {
    setCurrentPage(EstimatePanelPages.ATTACHMENT);
  };

  const handleSaveTnc = async (tnc: string) => {
    if (estimate && currentTeam?.id) {
      try {
        await updateEstimateTnc({
          teamId: currentTeam.id,
          estimateId: estimate.id,
          tnc
        }).unwrap();

        refreshEstimateDetails();
      } catch (error) {
        // Handle error if needed
      }
    }
  };

  const handleSaveNote = async (comment: string) => {
    if (estimate && currentTeam?.id) {
      try {
        await updateEstimateNote({
          teamId: currentTeam.id,
          estimateId: estimate.id,
          comment
        }).unwrap();
        refreshEstimateDetails();
      } catch (error) {
        // Handle error if needed
      }
    }
  };

  const handleAttachmentsSave = async updatedAttachments => {
    if (estimate && currentTeam?.id) {
      try {
        await updateEstimateAttachments({
          teamId: currentTeam.id,
          estimateId: estimate.id,
          attachments: updatedAttachments
        }).unwrap();
        refreshEstimateDetails();
      } catch (error) {
        // Handle error if needed
      }
    }
  };

  const handleEditRemindersClick = () => {
    setIsReminderModalOpen(true);
  };

  const handleCloseReminderModal = () => {
    setIsReminderModalOpen(false);
  };

  const handleSaveReminders = async (reminders: ReminderConfig[]) => {
    if (estimate && currentTeam?.id) {
      try {
        const apiReminders = transformEstimateRemindersToApiFormat(reminders, true);
        await updateEstimateReminders({
          teamId: currentTeam.id,
          estimateId: estimate.id,
          reminders: apiReminders
        }).unwrap();
        refreshEstimateDetails();
        setIsReminderModalOpen(false);
      } catch (error) {
        // Handle error if needed
      }
    }
  };

  return (
    <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={location?.state?.shouldAnimate}>
      {currentPage === EstimatePanelPages.DETAILS && (
        <EstimateDetailsPanel
          estimate={estimate}
          onCustomerClick={handleCustomerClick}
          onEditDescriptionClick={handleEditDescriptionClick}
          onEditExpirationClick={handleEditExpirationClick}
          onEditAssigneesClick={handleEditAssigneesClick}
          getTeamMemberNames={getTeamMemberNames}
          isRootState={isRootState}
          handleSidePanelClose={handleSidePanelClose}
          isEstimateByIdFetching={isEstimateByIdFetching}
          refetchEstimate={getEstimateById}
          handleDeleteEstimate={handleDeleteClick}
          handleMarkAsExpired={handleMarkAsExpired}
          handleArchiveEstimate={handleArchiveEstimate}
          isError={isEstimateByIdError}
          onEditNoteClick={handleEditNoteClick}
          onDeleteNoteClick={handleDeleteNoteClick}
          currentActionsPopoverId={currentActionsPopoverId}
          setCurrentActionsPopoverId={setCurrentActionsPopoverId}
          onViewHistoryClick={handleViewHistoryClick}
          isSingleMemberTeam={assignableTeamMembers.length === 1}
          estimateTimeline={timelineData?.data?.timelineList || []}
          onEditStatusClick={handleEditStatusClick}
          onEditLeadSourceClick={handleEditLeadSourceClick}
          onAddNoteClick={handleAddNoteClick}
          onEditTncClick={handleEditTncClick}
          onEditEstimateNoteClick={handleEditEstimateNoteClick}
          onAttachmentDetailClick={handleAttachmentDetailClick}
          handleMarkAsAccepted={handleMarkAsAccepted}
          refetchEstimatesList={refetchEstimatesList}
          onEditRemindersClick={handleEditRemindersClick}
        />
      )}
      {currentPage === EstimatePanelPages.EDIT_DESCRIPTION && estimate && (
        <EditEstimateDescriptionPanel
          description={estimate.description}
          onBackClick={goBackToDetails}
          onSave={handleSaveDescription}
          estimateId={estimate.id}
          teamId={currentTeam?.id}
        />
      )}

      {currentPage === EstimatePanelPages.HISTORY && estimate && (
        <EstimateTimelinePanel
          onBackClick={goBackToDetails}
          estimateTimeline={timelineData?.data?.timelineList || []} // Use the new timeline data
          estimateId={estimate.id}
          teamId={currentTeam.id}
          onEditNote={handleEditNoteClick}
          onDeleteNote={handleDeleteNoteClick}
          getEstimateTimeline={() => getEstimateTimeline(estimate.id)} // Optional: refetch logic
          notesInput={notesInput}
          setNotesInput={setNotesInput}
          isEditAllowed={estimate.status.toUpperCase() === EstimateStatus.PENDING}
        />
      )}

      {currentPage === EstimatePanelPages.STATUS && estimate && (
        <EditEstimateStatusPanel
          status={estimate.status as EstimateStatus}
          onBackClick={goBackToDetails}
          handleStatusChange={status => {
            setEditedStatus(status);
            setCurrentPage(EstimatePanelPages.STATUS_DETAILS);
          }}
        />
      )}

      {currentPage === EstimatePanelPages.STATUS_DETAILS && estimate && (
        <EditEstimateStatusDetailPanel
          status={editedStatus as EstimateStatus}
          onBackClick={goBackToDetails}
          onSave={handleUpdateStatus}
          isRootState={false}
          isActionInProgress={isUpdateEstimateStatusLoading}
        />
      )}

      {currentPage === EstimatePanelPages.LEAD_SOURCE && estimate && (
        <EditLeadSourcePanel
          leadSource={estimate.leadSource ?? ''}
          leadSourceType={estimate.leadSourceType ?? ''}
          onBackClick={goBackToDetails}
          onSaveClick={handleChangeLeadSource}
          isActionInProgress={isUpdateEstimateLeadSourceLoading}
        />
      )}

      {isEditNoteModalOpen.isOpen && estimate && (
        <CreateEditEstimateNoteModal
          defaultNote={notesInput}
          onClose={handleCloseEditNoteModal}
          isEdit={isEditNoteModalOpen.isEdit}
          currentNote={currentNote}
          teamId={currentTeam.id}
          estimateId={estimate.id}
          getEstimateTimeline={() => getEstimateTimeline(estimate.id)}
          setNotesInput={setNotesInput}
        />
      )}

      <GenericDeleteModal
        showModal={showDeleteModal}
        isActionInProgress={isDeleteEstimateLoading}
        title="Delete Estimate"
        subTitle="Are you sure you want to delete this estimate? This action cannot be undone."
        primaryBtnLabel="Delete"
        primaryBtnId="delete-estimate-confirm"
        onPrimaryBtnClick={handleDeleteEstimate}
        secondaryBtnLabel="Cancel"
        secondaryBtnId="delete-estimate-cancel"
        onSecondaryBtnClick={() => setShowDeleteModal(false)}
      />

      {/* Assignable Members Modal */}
      {openJobAssigneesModal && (
        <AssignableMembersModal
          list={assignableTeamMembers}
          onClose={handleCloseAssignMembersModal}
          selectedMembers={estimate?.assignees?.map(assignee => ({ ...assignee, merchantId: assignee.id })) || []}
          onSave={handleSaveAssignees} // Save selected members
        />
      )}

      {currentPage === EstimatePanelPages.TNC && estimate && (
        <EditEstimateTncPanel tnc={estimate.tnc} onBackClick={goBackToDetails} onSave={handleSaveTnc} />
      )}
      {currentPage === EstimatePanelPages.NOTE && estimate && (
        <EditEstimateNotePanel note={estimate.note} onBackClick={goBackToDetails} onSave={handleSaveNote} />
      )}
      {currentPage === EstimatePanelPages.ATTACHMENT && estimate && (
        <AttachmentViewDetailsPanel
          estimateAttachments={estimate.attachments}
          onBackClick={goBackToDetails}
          onSave={handleAttachmentsSave}
          canEdit={estimate && estimate?.status.toUpperCase() === EstimateStatus.PENDING}
        />
      )}
      {currentPage === EstimatePanelPages.EXPIRY_DATE && (
        <EditExpiryDatePanel
          selectedDate={estimate?.hasExpiry ? DateUtils.parseISODateString(estimate?.expiresAt) : null}
          onBackClick={goBackToDetails}
          onSave={handleSaveExpiryDate}
          isActionInProgress={isUpdateEstimateExpiryDateLoading}
        />
      )}

      {isReminderModalOpen && estimate && (
        <EditReminderModal
          isOpen={isReminderModalOpen}
          onClose={handleCloseReminderModal}
          onSave={handleSaveReminders}
          title="Estimate reminders"
          subtitle="Reminders will be sent to customers email"
          referenceDate={estimate.expiresAt}
          dateType={ReminderDateTypeLabel.EXPIRY_DATE}
          isLoading={isUpdateEstimateRemindersLoading}
          initialReminders={transformEstimateApiRemindersToComponentFormat(estimate?.reminders)}
        />
      )}
    </SidePanel>
  );
};

export default EstimatesPanel;
